import { toWorld, toView, type Projection } from './projection.ts';
import {
  parseCellId,
  toCellId,
  type GameState,
  type MapDef,
  type Point,
  type ShipState,
} from '@brig/rules-engine';
import type { Heading } from '../ships/sides.ts';
import { ART_KEEL, ART_SCALE, ISO_ART, pairBeam, type MapDir } from './shipArt.ts';

/**
 * Board geometry in world units: one cell is CELL units square, the map's top-left corner is
 * (0, 0). The camera maps world units to CSS px.
 */
export const CELL = 22;
/** Two ships sharing a cell are drawn at this fraction of the cell, offset diagonally. */
export const PAIR_SCALE = 0.8;
/**
 * Where ships stand up, a pair stands side by side across the screen instead (the cell's
 * diagonal would put one straight behind the other): keels this many paired boxes apart.
 */
const PAIR_SPREAD = 0.75;
/** Where ships stand up, keels sit this many boxes below the cell's centre on screen. */
const KEEL_DROP = 0.25;

export function worldSize(map: MapDef): { width: number; height: number } {
  return { width: map.width * CELL, height: map.height * CELL };
}

export function cellOrigin(cell: string): { x: number; y: number } {
  const p = parseCellId(cell) ?? { x: 0, y: 0 };
  return { x: p.x * CELL, y: p.y * CELL };
}

export function cellCenter(cell: string): { x: number; y: number } {
  const o = cellOrigin(cell);
  return { x: o.x + CELL / 2, y: o.y + CELL / 2 };
}

/** The cell under a world point, or null outside the map. */
export function cellAt(map: MapDef, wx: number, wy: number): Point | null {
  const x = Math.floor(wx / CELL);
  const y = Math.floor(wy / CELL);
  return x >= 0 && y >= 0 && x < map.width && y < map.height ? { x, y } : null;
}

/** Where a ship sits in its cell: alone (0), or first (-1, top-left) / second (1) of a pair. */
export type Slot = -1 | 0 | 1;

export function slotBox(slot: Slot): { offset: number; box: number } {
  const box = slot === 0 ? CELL : CELL * PAIR_SCALE;
  return { offset: slot === 1 ? CELL - box : 0, box };
}

/** Afloat ships grouped by cell, in state order. */
export function shipsByCell(ships: readonly ShipState[]): Map<string, ShipState[]> {
  const out = new Map<string, ShipState[]>();
  for (const s of ships) if (!s.sunk) out.set(s.cell, [...(out.get(s.cell) ?? []), s]);
  return out;
}

/** The painted fleet: a pair's hulls this many cells apart from the middle, beam to beam. */
const ISO_PAIR_OFFSET = 0.24;

const SOUTH: MapDir = { x: 0, y: 1 };

/** The waterline in a ship's 32×32 token box, as a fraction of the box: where it meets the sea. */
export const KEEL = 27 / 32;

/**
 * Where a ship meets the sea, in world units: the middle of its keel. In the flat view a pair
 * sits on the cell's diagonal. Where ships stand up, a ship stands under the cell's centre on
 * screen, a pair left (-1) and right (1) of it, at the same depth.
 */
export function shipAnchor(
  cell: string,
  slot: Slot,
  proj: Projection,
  beam: MapDir = SOUTH,
): { x: number; y: number } {
  // The painted fleet stands with its hull in the middle of the cell; a pair lies beam to
  // beam, side by side along `beam` (across the way the hulls lie, shipArt.ts pairBeam).
  if (ISO_ART) {
    const c = cellCenter(cell);
    const d = slot * ISO_PAIR_OFFSET * CELL;
    return { x: c.x + beam.x * d, y: c.y + beam.y * d };
  }
  const o = cellOrigin(cell);
  const { offset, box } = slotBox(slot);
  if (!proj.upright) return { x: o.x + offset + box / 2, y: o.y + offset + box * KEEL };
  const c = cellCenter(cell);
  const mid = toView(proj, c.x, c.y);
  return toWorld(proj, mid.x + (slot * PAIR_SPREAD * box) / 2, mid.y + KEEL_DROP * box);
}

export function slotOf(index: number, count: number): Slot {
  return count === 1 ? 0 : index === 0 ? -1 : 1;
}

export type Hit =
  { readonly kind: 'unit'; readonly id: string } | { readonly kind: 'cell'; readonly id: string };

export interface PickInput {
  readonly state: GameState;
  readonly ships: ReadonlyMap<string, readonly ShipState[]>;
  readonly moveCells: ReadonlySet<string>;
  readonly portCells: ReadonlySet<string>;
  readonly targets: ReadonlySet<string>;
  readonly volleyTarget: string | null;
  /**
   * Whether a ship's artwork is drawn at a point of its token box (0..1 across, 0..1 down).
   * Without it a standing ship is hit anywhere in its box.
   */
  readonly solid?: (shipId: string, u: number, v: number) => boolean;
  /** Each ship's heading, to stand a pair beam to beam as drawn (default east). */
  readonly facing?: ReadonlyMap<string, Heading>;
}

/**
 * The heading a ship is drawn with. Two ships of one cell always lie parallel, never at an
 * angle to each other: both take the heading of the one that was there first.
 */
export function drawnHeading(
  ship: ShipState,
  cellShips: readonly ShipState[],
  facing: ReadonlyMap<string, Heading> | undefined,
): Heading {
  const lead = cellShips.length > 1 ? (cellShips[0] ?? ship) : ship;
  return facing?.get(lead.id) ?? 'E';
}

/** Along which way a cell's pair stands, from the pair's drawn heading. */
export function cellBeam(
  ships: readonly ShipState[],
  facing: ReadonlyMap<string, Heading> | undefined,
  proj: Projection,
): MapDir {
  const first = ships[0];
  return first ? pairBeam(first.cls, drawnHeading(first, ships, facing), proj) : SOUTH;
}

/**
 * What a click at a world point means, topmost first: a fort's target ring, a highlighted
 * cell (move or port), a ship (the second of a pair owns the lower-right half), a fort, the cell.
 */
export function pick(input: PickInput, wx: number, wy: number): Hit | null {
  const p = cellAt(input.state.map, wx, wy);
  if (!p) return null;
  const cell = toCellId(p);
  const fort = input.state.forts.find((f) => f.cell === cell);
  if (fort && (input.targets.has(fort.id) || input.volleyTarget === fort.id))
    return { kind: 'unit', id: fort.id };
  if (input.moveCells.has(cell) || input.portCells.has(cell)) return { kind: 'cell', id: cell };
  const ships = input.ships.get(cell) ?? [];
  const [first, second] = ships;
  if (first && second) {
    const fx = wx / CELL - p.x;
    const fy = wy / CELL - p.y;
    return { kind: 'unit', id: (fx + fy > 1 ? second : first).id };
  }
  if (first) return { kind: 'unit', id: first.id };
  if (fort) return { kind: 'unit', id: fort.id };
  return { kind: 'cell', id: cell };
}

/**
 * The ship whose standing artwork is under a point in view space, nearest first (of a pair,
 * the right one, drawn over the left), or null. Only in the 2.5D views, where ships stand up:
 * a hit here is the ship itself, not just its cell, so even one of a pair is meant for sure.
 */
export function shipArtAt(
  input: PickInput,
  proj: Projection,
  vx: number,
  vy: number,
): { id: string; cell: string } | null {
  if (!proj.upright) return null;
  let best: { id: string; cell: string; depth: number } | null = null;
  for (const [cell, ships] of input.ships) {
    for (const [i, ship] of ships.entries()) {
      const slot = slotOf(i, ships.length);
      const drawn = slotBox(slot).box * ART_SCALE;
      const a = shipAnchor(cell, slot, proj, cellBeam(ships, input.facing, proj));
      const v = toView(proj, a.x, a.y);
      const u = (vx - v.x) / drawn + 0.5;
      const t = (vy - v.y) / drawn + ART_KEEL;
      const inside = u >= 0 && u <= 1 && t >= 0 && t <= 1 && (input.solid?.(ship.id, u, t) ?? true);
      if (inside && (!best || v.y > best.depth - 1e-6)) best = { id: ship.id, cell, depth: v.y };
    }
  }
  return best && { id: best.id, cell: best.cell };
}

/**
 * `pick` for a point in view space. In the 2.5D views ships stand up out of their cells, so
 * their artwork is hit first (`solid`, so a low hull in front does not cover the sails behind
 * it), nearest first (of a pair, the right one, drawn over the left); a hull standing on a
 * highlighted cell still means that cell. Elsewhere the point goes down to the sea.
 */
export function pickInView(input: PickInput, proj: Projection, vx: number, vy: number): Hit | null {
  const w = toWorld(proj, vx, vy);
  const art = shipArtAt(input, proj, vx, vy);
  if (art) {
    return input.moveCells.has(art.cell) || input.portCells.has(art.cell)
      ? { kind: 'cell', id: art.cell }
      : { kind: 'unit', id: art.id };
  }
  return pick(input, w.x, w.y);
}
